import { Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

export interface CnesVaga {
  cnes: string;
  qtdVagas: number;
  nomeEstabelecimento?: string;
  nivelVaga?: { id: number; nome: string };
}

interface CnesVagaGrupo {
  cnes: string;
  nivelVaga?: { id: number; nome: string };
  nomeEstabelecimento?: string;
  totalVagas: number;
  selecionado: boolean;
  qtdVagas: number;
}

@Component({
  selector: 'app-cnes-vaga-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="modal-header">
      <h5 class="modal-title">Atribuir Vagas — {{ tipoNome }}</h5>
      <button type="button" class="br-button circle small" (click)="activeModal.dismiss()">
        <i class="fas fa-times"></i>
      </button>
    </div>
    <div class="modal-body">
      <div class="alert alert-info">
        Marque os estabelecimentos onde deseja indicar vagas e informe a quantidade de cada um. Ao final, clique em <strong>Salvar</strong>.
      </div>

      @for (grupo of grupos; track grupo.cnes) {
        <div class="border rounded p-2 mb-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div class="d-flex align-items-center">
            <input
              type="checkbox"
              class="form-check-input me-2"
              [id]="'cnes-' + grupo.cnes"
              [(ngModel)]="grupo.selecionado"
              (ngModelChange)="onToggle(grupo)"
            />
            <label [for]="'cnes-' + grupo.cnes" class="mb-0">
              <div>
                <strong>{{ grupo.cnes }}</strong>
                <span class="badge ms-2" [ngClass]="badgeClass(grupo)">{{ nivelLabel(grupo) }}</span>
              </div>
              @if (grupo.nomeEstabelecimento) {
                <div>{{ grupo.nomeEstabelecimento }}</div>
              }
              <div class="text-muted small">Teto de vagas: {{ grupo.totalVagas }}</div>
            </label>
          </div>
          <div class="d-flex align-items-center">
            <label class="me-2 mb-0" [for]="'qtd-' + grupo.cnes">Vagas:</label>
            <input
              type="number"
              [id]="'qtd-' + grupo.cnes"
              class="form-control form-control-sm"
              style="width: 80px"
              [(ngModel)]="grupo.qtdVagas"
              [disabled]="!grupo.selecionado"
              [min]="1"
              [max]="grupo.totalVagas"
              (ngModelChange)="onQtdChange(grupo)"
            />
          </div>
        </div>
      }

      @if (grupos.length === 0) {
        <div class="text-center text-muted fst-italic">Nenhum estabelecimento disponível.</div>
      }
    </div>
    <div class="modal-footer">
      <button type="button" class="br-button secondary" (click)="activeModal.dismiss()">Cancelar</button>
      <button type="button" class="br-button primary" (click)="confirmar()">Salvar</button>
    </div>
  `,
})
export class CnesVagaModalComponent implements OnInit {
  activeModal = inject(NgbActiveModal);

  @Input() tipoNome = '';
  @Input() vagas: { id: number; vagaPrograma: any }[] = [];
  @Input() selecionados: CnesVaga[] = [];

  grupos: CnesVagaGrupo[] = [];

  ngOnInit(): void {
    this.grupos = this.agruparPorCnes(this.vagas);
  }

  nivelLabel(grupo: CnesVagaGrupo): string {
    switch (grupo.nivelVaga?.id) {
      case 1: return 'Municipal';
      case 2: return 'Estadual';
      case 4: return 'Dupla';
      default: return grupo.nivelVaga?.nome ?? '';
    }
  }

  badgeClass(grupo: CnesVagaGrupo): string {
    switch (grupo.nivelVaga?.id) {
      case 1: return 'bg-success-subtle text-success-emphasis';
      case 2: return 'bg-primary-subtle text-primary-emphasis';
      default: return 'bg-warning-subtle text-warning-emphasis';
    }
  }

  onToggle(grupo: CnesVagaGrupo): void {
    if (grupo.selecionado && !grupo.qtdVagas) {
      grupo.qtdVagas = 1;
    }
  }

  onQtdChange(grupo: CnesVagaGrupo): void {
    if (grupo.qtdVagas > grupo.totalVagas) {
      grupo.qtdVagas = grupo.totalVagas;
    } else if (grupo.qtdVagas < 1) {
      grupo.qtdVagas = 1;
    }
  }

  confirmar(): void {
    const selecionados = this.grupos
      .filter((g) => g.selecionado)
      .map((g) => ({ cnes: g.cnes, qtdVagas: g.qtdVagas, nomeEstabelecimento: g.nomeEstabelecimento, nivelVaga: g.nivelVaga }));

    this.activeModal.close(selecionados);
  }

  private agruparPorCnes(vagas: { id: number; vagaPrograma: any }[]): CnesVagaGrupo[] {
    const grupos = new Map<string, CnesVagaGrupo>();

    for (const vaga of vagas) {
      const cnes = vaga.vagaPrograma?.cnes;
      if (!cnes) continue;

      const existente = grupos.get(cnes);
      if (existente) {
        existente.totalVagas += 1;
        continue;
      }

      const selecaoAnterior = this.selecionados.find((s) => s.cnes === cnes);
      const estabelecimento = vaga.vagaPrograma?.estabelecimento;
      grupos.set(cnes, {
        cnes,
        nivelVaga: vaga.vagaPrograma?.nivelVaga,
        nomeEstabelecimento: estabelecimento?.nomeFantasia || estabelecimento?.razaoSocial,
        totalVagas: 1,
        selecionado: !!selecaoAnterior,
        qtdVagas: selecaoAnterior?.qtdVagas ?? 1,
      });
    }

    return [...grupos.values()];
  }
}
